/*
  eslint-disable camelcase
*/

import { Action, ActionTypes } from '../actions';
import {
  BeaconChainStatus,
  DepositStatus,
  TransactionStatus,
} from '../actions/depositFileActions';
import { BeaconDepositStatus } from '../../utils/depositQueueTracker';
import { ValidatorDepositStatus } from '../../utils/validatorStatusChecker';

export interface DepositKeyInterface {
  pubkey: string;
  withdrawal_credentials: string;
  amount: number;
  signature: string;
  deposit_message_root: string;
  deposit_data_root: string;
  fork_version: string;
  deposit_cli_version: string;
  transactionStatus: TransactionStatus;
  txHash?: string;
  depositStatus: DepositStatus;
  // Beacon chain deposit tracking
  beaconDepositStatus: BeaconDepositStatus;
  depositIndex: number | null;
  beaconProcessingIndex: number | null;
  queuePosition: number | null;
  validatorIndex: number | null;
  // Comprehensive validator status (from validatorStatusChecker)
  validatorDepositStatus: ValidatorDepositStatus;
  beaconStatus: string | null;
  beaconBalance: string | null;
  execDeposited: boolean;
  execTxHash: string | null;
  execBlockNumber: number | null;
  execDepositIndex: number | null;
}

export interface DepositFileInterface {
  name: string;
  beaconChainApiStatus: BeaconChainStatus;
  keys: DepositKeyInterface[];
}

const initialState: DepositFileInterface = {
  name: '',
  beaconChainApiStatus: BeaconChainStatus.HEALTHY,
  keys: [],
};

interface BeaconchainDepositDataInterface {
  amount: number;
  block_number: number;
  block_ts: number;
  from_address: string;
  merkletree_index: string;
  publickey: string;
  removed: boolean;
  signature: string;
  tx_hash: string;
  tx_index: number;
  tx_input: string;
  valid_signature: boolean;
  withdrawal_credentials: string;
}

export interface BeaconchainDepositInterface {
  data: BeaconchainDepositDataInterface[];
  status: string;
}

const updateKeyByPubkey = (
  keys: DepositKeyInterface[],
  pubkey: string,
  updater: (key: DepositKeyInterface) => DepositKeyInterface
): DepositKeyInterface[] | null => {
  const index = keys.findIndex(k => k.pubkey === pubkey);
  if (index === -1) return null;
  return keys.map((key, i) => (i === index ? updater(key) : key));
};

export const depositFileReducer = (
  state: DepositFileInterface = initialState,
  action: Action
): DepositFileInterface => {
  switch (action.type) {
    case ActionTypes.updateDepositFileName:
      return { ...state, name: action.payload };

    case ActionTypes.updateDepositFileKeys:
      return { ...state, keys: action.payload };

    case ActionTypes.updateTransactionStatus: {
      const updatedKeys = updateKeyByPubkey(
        state.keys,
        action.payload.pubkey,
        key => ({
          ...key,
          transactionStatus: action.payload.status,
          ...(action.payload.txHash && { txHash: action.payload.txHash }),
        })
      );
      return updatedKeys ? { ...state, keys: updatedKeys } : state;
    }

    case ActionTypes.updateDepositStatus: {
      const updatedKeys = updateKeyByPubkey(
        state.keys,
        action.payload.pubkey,
        key => ({
          ...key,
          depositStatus: action.payload.depositStatus,
        })
      );
      return updatedKeys ? { ...state, keys: updatedKeys } : state;
    }

    case ActionTypes.updateBeaconChainAPIStatus:
      return { ...state, beaconChainApiStatus: action.payload };

    case ActionTypes.updateBeaconDepositStatus: {
      const updatedKeys = updateKeyByPubkey(
        state.keys,
        action.payload.pubkey,
        key => ({
          ...key,
          beaconDepositStatus: action.payload.beaconDepositStatus,
          depositIndex: action.payload.depositIndex,
          beaconProcessingIndex: action.payload.beaconProcessingIndex,
          queuePosition: action.payload.queuePosition,
          validatorIndex: action.payload.validatorIndex,
        })
      );
      return updatedKeys ? { ...state, keys: updatedKeys } : state;
    }

    case ActionTypes.updateValidatorDepositStatus: {
      const updatedKeys = updateKeyByPubkey(
        state.keys,
        action.payload.pubkey,
        key => ({
          ...key,
          validatorDepositStatus: action.payload.validatorDepositStatus,
          beaconStatus: action.payload.beaconStatus,
          beaconBalance: action.payload.beaconBalance,
          execDeposited: action.payload.execDeposited,
          execTxHash: action.payload.execTxHash,
          execBlockNumber: action.payload.execBlockNumber,
          execDepositIndex: action.payload.execDepositIndex,
          // Also update validatorIndex if we have it from beacon
          ...(action.payload.beaconIndex !== null && {
            validatorIndex: action.payload.beaconIndex,
          }),
        })
      );
      return updatedKeys ? { ...state, keys: updatedKeys } : state;
    }

    default:
      return state;
  }
};
